import { useCallback, useMemo } from 'react'
import { clearQueryCache } from '@/shared/api/queryClient'
import { useLocalAuth } from './LocalAuthContext'
import { useOkta } from './OktaContext'

export type AuthUser = {
  name: string
  email?: string | undefined
}

type Auth = {
  isAuthenticated: boolean
  user: AuthUser | null
  logout: () => void
}

/** The one place the rest of the app asks "who is signed in?". */
export const useAuth = (): Auth => {
  const { oktaAuth, oktaUserInfo, isAuthenticated: isOkta } = useOkta()
  const { isAuthenticated: isLocal, logout: localLogout } = useLocalAuth()

  const logout = useCallback(() => {
    clearQueryCache()
    if (isOkta) {
      // tokenManager.clear() emits the SDK's "removed" event, which is what
      // updates authStateManager (and so isAuthenticated). Skips Okta's hosted
      // logout redirect, which needs a registered post-logout redirect URI.
      oktaAuth.n26OktaAuth.tokenManager.clear()
    } else if (isLocal) {
      localLogout()
    }
  }, [isOkta, isLocal, oktaAuth, localLogout])

  return useMemo(() => {
    const user: AuthUser | null = isOkta
      ? {
          name: oktaUserInfo?.name ?? oktaUserInfo?.email ?? 'Signed in',
          email: oktaUserInfo?.name ? oktaUserInfo.email : undefined,
        }
      : isLocal
        ? { name: 'Local session' }
        : null
    return { isAuthenticated: isOkta || isLocal, user, logout }
  }, [isOkta, isLocal, oktaUserInfo, logout])
}
